import React from 'react';
import { toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';

import cn from '../../../utils/cn';
import ToastBody from './ToastBody';
import CloseButton from './CloseButton';
import styles from './Toast.module.scss';

toast.configure();

const ANNOUNCER_ID = 'toast-announcer';

const announcerRegion = () => {
  let region = document.getElementById(ANNOUNCER_ID);
  if (!region) {
    region = document.createElement('div');
    region.id = ANNOUNCER_ID;
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.className = 'sr-only';
    document.body.appendChild(region);
  }
  return region;
};

// AT only reliably announces additions to live regions already in the DOM; react-toastify
// mounts its container together with the first toast, so SRs miss it (WCAG 4.1.3).
// Create the announcer eagerly at load and mirror toast messages into it.
if (typeof document !== 'undefined' && document.body) announcerRegion();

const announce = (msg, heading) => {
  const text = [heading, msg].filter(part => part && typeof part === 'string').join(' ');
  if (!text) return;

  const region = announcerRegion();
  region.textContent = '';
  window.setTimeout(() => { region.textContent = text; }, 50);
};

// `announcement` covers a JSX `msg`: announce() keeps only string parts.
export default function showToast(msg, {
  announcement, autoClose = 5000, closeOnClick = true, containerId, iconStyle,
  msgHeading = '', msgType = 'success',
  position = 'bottom-left', toastId,
} = {}) {
  announce(announcement ?? msg, msgHeading);

  const options = {
    autoClose,
    className: cn(styles.toast, { [styles.circleStyling]: iconStyle === 'circle' }),
    closeButton: <CloseButton centeredStyle={iconStyle === 'circle'} />,
    closeOnClick,
    containerId,
    draggable: true,
    hideProgressBar: true,
    pauseOnHover: true,
    position,
    toastId,
    transition: Slide
  };

  return toast(
    <ToastBody
      body={msg}
      heading={msgHeading}
      iconStyle={iconStyle}
      msgType={msgType}
    />,
    options
  );
}

export function dismissToast(toastId) {
  toast.dismiss(toastId);
}
